import { CSSProperties, FC, useEffect, useRef, useState } from 'react';

import Hls from 'hls.js';

import IconButton from '@components/buttons/IconButton';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';

interface VideoPlayerProps {
  url: string;
  /** When true (card is visible), start playing. When false, pause. */
  autoplay: boolean;
  aspectRatio: number;
  containerStyle?: CSSProperties;
}

export const VideoPlayer: FC<VideoPlayerProps> = ({
  url,
  autoplay,
  aspectRatio,
  containerStyle = {},
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);

  // Attach HLS source. Uses native playback on Safari, hls.js elsewhere.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = url;
      return () => {
        video.removeAttribute('src');
        video.load();
      };
    }

    if (Hls.isSupported()) {
      const hls = new Hls({ maxBufferLength: 10, maxMaxBufferLength: 20, startLevel: 0 });
      hls.loadSource(url);
      hls.attachMedia(video);
      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (data.fatal) {
          if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
            hls.startLoad();
          } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
            hls.recoverMediaError();
          } else {
            hls.destroy();
          }
        }
      });
      return () => hls.destroy();
    }
  }, [url]);

  // Visibility drives play/pause. Scrolling out always pauses.
  useEffect(() => {
    setIsPlaying(autoplay);
  }, [autoplay]);

  // Sync the actual video element with isPlaying state.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (isPlaying) {
      video.play().catch(() => setIsPlaying(false));
    } else {
      video.pause();
    }
  }, [isPlaying]);

  const handleClick = () => {
    setIsPlaying((prev) => !prev);
  };

  const handleToggleMuted = () => {
    setIsMuted((prev) => !prev);
  };

  const handleRestart = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
    }
  };

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        aspectRatio,
        background: '#000',
        cursor: 'pointer',
        display: 'block',
        position: 'relative',
        ...containerStyle,
      }}
    >
      <div className="p-3 d-flex gap-2" style={{ position: 'absolute', right: 0, bottom: 0 }}>
        <IconButton color="neutral" variant="smooth" onClick={handleToggleMuted}>
          <FontAwesomeIcon name={isMuted ? 'volume-off' : 'volume-up'} />
        </IconButton>

        <IconButton color="neutral" variant="smooth" onClick={handleRestart}>
          <FontAwesomeIcon name="undo" />
        </IconButton>
      </div>
      <div onClick={handleClick}>
        <video
          ref={videoRef}
          style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover' }}
          playsInline
          muted={isMuted}
          loop
          preload="metadata"
        />
      </div>
    </div>
  );
};
